/**
 * Affiliate links: bedly.ma/<any page>?ref=CODE.
 *
 * The code is remembered for thirty days, because people rarely sign up on the
 * first visit — they follow a partner's link, look around, come back a few days
 * later. Stored in this browser only; nothing is sent until they actually sign
 * up, and then only as the code they could have typed themselves.
 *
 * Every access is wrapped: storage can be unavailable (private windows,
 * blocked site data), and losing an attribution is better than breaking signup.
 */

const KEY = 'bedly.ref';
const THIRTY_DAYS = 30 * 24 * 60 * 60 * 1000;

/** Called once when the app starts: keeps a ?ref= from the landing URL. */
export function captureReferral(search: string = window.location.search): void {
  const code = new URLSearchParams(search).get('ref')?.trim();
  if (!code || !/^[A-Za-z0-9_-]{3,32}$/.test(code)) return;
  try {
    localStorage.setItem(KEY, JSON.stringify({ code: code.toUpperCase(), at: Date.now() }));
  } catch {
    // No storage: the code still reaches signup through the URL itself.
  }
}

/** The remembered code, if it is still within its thirty days. */
export function getReferral(): string {
  const fromUrl = new URLSearchParams(window.location.search).get('ref')?.trim();
  if (fromUrl) return fromUrl.toUpperCase();
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return '';
    const { code, at } = JSON.parse(raw) as { code?: string; at?: number };
    if (!code || !at || Date.now() - at > THIRTY_DAYS) {
      localStorage.removeItem(KEY);
      return '';
    }
    return code;
  } catch {
    return '';
  }
}

/** After a signup has used it, so a second account on this browser is not attributed too. */
export function clearReferral(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    // Nothing to clear.
  }
}
